import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject } from '@angular/core';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { IInvitationDialogData } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LocalStorageService } from '../../../../../aumico-lib-core-fe/src/lib/services/localStorage.service';

@UntilDestroy()
@Component({
    selector: 'app-invite-dialog',
    templateUrl: './invite-dialog.component.html',
    styleUrls: ['./invite-dialog.component.scss'],
})
export class InviteDialogComponent {
    emailChips: string[] = [];
    readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    readonly ownEmail = this.localStorageService.getCurrentUserEmail();

    emailControl = new FormControl('', [Validators.required, Validators.email]);
    message = new FormControl('', [Validators.required, Validators.maxLength(1000)]);

    constructor(
        public dialogRef: MatDialogRef<InviteDialogComponent>,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA) public data: IInvitationDialogData,
        private localStorageService: LocalStorageService,
    ) {}

    closeDialog(): void {
        this.dialogRef.close();
    }

    onSaveAndInviteClick(): void {
        // Create an array that combines both the current email input AND all chips
        const emails: string[] = [];

        // Add the current email from the form control if it exists and is valid
        if (this.emailControl.value && this.emailControl.value.trim()) {
            const currentEmail = this.emailControl.value.trim().toLowerCase();
            if (this.emailControl.valid && !emails.includes(currentEmail)) {
                emails.push(currentEmail);
            }
        }

        // Add all emails from the chips array
        emails.push(...this.emailChips);

        // Remove any duplicates (in case the current email is also in chips)
        const uniqueEmails = [...new Set(emails)];

        // Validate that we have at least one email
        if (uniqueEmails.length === 0) {
            this.emailControl.markAsTouched();
            return;
        }

        const messageValue = this.message.value || '';

        // Call the action with the combined emails
        if (this.data.hasMessage) {
            this.data.action(uniqueEmails, messageValue);
        } else {
            this.data.action(uniqueEmails);
        }

        // Close the dialog after successful submission
        this.closeDialog();
    }

    onEmailKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            // Only add chip if the current email is valid
            if (this.emailControl.valid) {
                this.addChip();
            } else {
                // Mark as touched to show validation errors
                this.emailControl.markAsTouched();
            }
        }
    }

    addChip(): void {
        const input = this.emailControl.value;
        const value = input.toLocaleLowerCase();

        // Add chip only if it's a valid email address and doesn't already exist
        if ((value || '').trim() && !this.emailChips.includes(value.trim()) && (value || '').trim() !== this.ownEmail) {
            this.emailChips.push(value.trim());
        }

        // Reset the input value
        if (input) {
            this.emailControl.setValue('');
        }

        this.updateEmailValidators();
    }

    removeChip(email: string): void {
        const index = this.emailChips.indexOf(email);
        if (index >= 0) {
            this.emailChips.splice(index, 1);
        }
        this.updateEmailValidators();
    }

    private updateEmailValidators(): void {
        if (this.emailChips.length > 0) {
            // Remove required validator if we have at least one chip
            this.emailControl.setValidators([Validators.email]);
        } else {
            // Add required validator back if no chips exist
            this.emailControl.setValidators([Validators.required, Validators.email]);
        }

        // Update the validity status
        this.emailControl.updateValueAndValidity();
    }
}
